import type { ConversationMessage, InboxConversationItem } from '../types/messages';
import type { InvestorProfile, StartupProfile } from '../types/firestore';

/**
 * In-platform PRAIRI Team welcome shown to current investors in Messages.
 * Replaces the short July 2024/2025 “Welcome to PRAIRI’s new 3.1…” thread body.
 * New investors who have no PRAIRI Team thread yet get a local welcome conversation.
 */

export const SYNTHETIC_PRAIRI_TEAM_CONVERSATION_ID = 'prairi-team-welcome';
export const SYNTHETIC_PRAIRI_TEAM_STARTUP_ID = 'prairi-team';
/** Older than real threads so a new message from someone else stays above PRAIRI. */
export const SYNTHETIC_PRAIRI_TEAM_WELCOME_AT = '2020-01-01T00:00:00.000Z';

/** The link line in the investor message. The inbox turns it into a link to the dashboard. */
export const PRAIRI_TEAM_INVESTOR_CTA_LINE = 'EXPLORE PRAIRI V3.1';

export const PRAIRI_TEAM_WELCOME_MESSAGE = `I am so excited to announce that PRAIRI V3.1 IS HERE!

Discover now what's new and how we help you find relevant startups faster:

NEW DISCOVERY DASHBOARD
- See more startups at once.
- Preview key details before opening full profiles.

SMARTER MATCHING
- Your deal flow gets even more focused as you interact with startups.

DIRECT FOUNDER MESSAGING
- Message founders in one click, without sharing your email address and phone number.

${PRAIRI_TEAM_INVESTOR_CTA_LINE}

To your success,
Peter


P.S. Have a question or an idea to improve your startup discovery experience? Just reply to this message.`;

export const PRAIRI_TEAM_WELCOME_PREVIEW =
  "I am so excited to announce that PRAIRI V3.1 IS HERE!\nDiscover now what's new and how we help you find relevant startups faster:";

/**
 * In-platform PRAIRI message for investors who are new to PRAIRI.
 * Signed "The PRAIRI Team", as in the design, so the spaced sign-off is not applied to it.
 */
export const PRAIRI_TEAM_NEW_INVESTOR_WELCOME_MESSAGE = `WELCOME TO PRAIRI!

Here is where you can discover relevant startups faster, and beyond your network.

Have a question? Simply reply to this message.

The PRAIRI Team

P.S. Is there’s anything else we can build to make startup discovery better for you? Let us know!`;

export const PRAIRI_TEAM_NEW_INVESTOR_WELCOME_PREVIEW =
  'WELCOME TO PRAIRI!\nHere is where you can discover relevant startups faster, and beyond your network.';

/** The link line in the startup message. The inbox turns it into a link to the startup profile. */
export const PRAIRI_TEAM_STARTUP_CTA_LINE = 'UPDATE YOUR PROFILE AND PITCH DECK →';

/**
 * In-platform PRAIRI message for existing startups. It replaces the older 3.1 welcome
 * in their PRAIRI thread, the same way the investor message does for investors.
 */
export const PRAIRI_TEAM_STARTUP_WELCOME_MESSAGE = `I am so excited to share 2 updates that could help you get discovered by the right investors.

We're onboarding many new investors to PRAIRI!

And, our newly launched PRAIRI v3.1, makes it easier than ever for investors to discover relevant startups, benefit from smarter matching, and message founders directly.

So now is the time to sharpen your profile:

BE SEEN AND REACHED BY THE RIGHT INVESTORS

- Add your founder background (new field).
- Share your latest achievements on your profile and pitch deck.
- You can even add a second relevant industry.

${PRAIRI_TEAM_STARTUP_CTA_LINE}

To your success,Peter

P.S. Have a question or an idea to improve PRAIRI? Just reply here.`;

export const PRAIRI_TEAM_STARTUP_WELCOME_PREVIEW =
  "I am so excited to share 2 updates that could help you get discovered by the right investors.\nWe're onboarding many new investors to PRAIRI!";

/**
 * In-platform PRAIRI message for startups that have no PRAIRI thread yet.
 * Signed "The PRAIRI Team", as in the design, so the spaced sign-off is not applied to it.
 */
export const PRAIRI_TEAM_NEW_STARTUP_WELCOME_MESSAGE = `WELCOME TO PRAIRI!

Here is where you get discovered by investors beyond your network, without having to chase them.

Have a question? Simply reply to this message.

The PRAIRI Team

P.S. Is there’s anything else we can build to make startup discovery better for you? Let us know!`;

export const PRAIRI_TEAM_NEW_STARTUP_WELCOME_PREVIEW =
  'WELCOME TO PRAIRI!\nHere is where you get discovered by investors beyond your network, without having to chase them.';

/**
 * Startups that registered on or after this date get the new-startup welcome. Everyone else
 * gets the existing-startups message. Left unset until the date is decided, so for now every
 * startup gets the existing-startups message.
 */
export const NEW_STARTUP_WELCOME_FROM: string | null = null;

function timeOf(value: unknown): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string' || typeof value === 'number') return Date.parse(String(value));
  if (value && typeof value === 'object') {
    const stamp = value as { seconds?: number; _seconds?: number };
    const seconds = stamp.seconds ?? stamp._seconds;
    if (typeof seconds === 'number') return seconds * 1000;
  }
  return Number.NaN;
}

/** Same as above, for investors. Unset until the date is decided. */
export const NEW_INVESTOR_WELCOME_FROM: string | null = null;

export function isNewStartupAccount(
  createdAt: unknown,
  since: string | null = NEW_STARTUP_WELCOME_FROM
): boolean {
  return registeredOnOrAfter(createdAt, since);
}

export function isNewInvestorAccount(
  createdAt: unknown,
  since: string | null = NEW_INVESTOR_WELCOME_FROM
): boolean {
  return registeredOnOrAfter(createdAt, since);
}

function registeredOnOrAfter(createdAt: unknown, since: string | null): boolean {
  if (!since) return false;
  const created = timeOf(createdAt);
  const cutoff = Date.parse(since);
  return !Number.isNaN(created) && !Number.isNaN(cutoff) && created >= cutoff;
}

export type PrairiViewerType = 'investor' | 'startup';

function welcomeBodyFor(viewer: PrairiViewerType): string {
  return viewer === 'startup' ? PRAIRI_TEAM_STARTUP_WELCOME_MESSAGE : PRAIRI_TEAM_WELCOME_MESSAGE;
}

function welcomePreviewFor(viewer: PrairiViewerType): string {
  return viewer === 'startup' ? PRAIRI_TEAM_STARTUP_WELCOME_PREVIEW : PRAIRI_TEAM_WELCOME_PREVIEW;
}

/** True when the thread already holds the investor message (stored or shown). */
export function hasInvestorWelcomeMessage(bodies: string[]): boolean {
  return bodies.some(
    body => body.includes('PRAIRI V3.1 IS HERE') || body.includes('PRAIRI 3.1 is here')
  );
}

/** True when the thread already holds the startup message (stored or shown). */
export function hasStartupWelcomeMessage(bodies: string[]): boolean {
  return bodies.some(
    body =>
      body.includes('help you get discovered by the right investors') ||
      isLegacyPrairiTeamWelcome(body)
  );
}

export function isPrairiTeamName(value?: string | null): boolean {
  const name = (value || '').trim().toLowerCase();
  return name === 'prairi team' || name === 'prairi';
}

export function isPrairiTeamCatalogStartup(item: {
  companyName?: string | null;
  slogan?: string | null;
}): boolean {
  if (isPrairiTeamName(item.companyName)) return true;
  return (item.slogan || '').toLowerCase().includes('redefining fundraising');
}

export function isPrairiTeamParticipant(user: {
  name?: string | null;
  companyName?: string | null;
}): boolean {
  return isPrairiTeamName(user.name) || isPrairiTeamName(user.companyName);
}

export function isLegacyPrairiTeamWelcome(body: string): boolean {
  const text = body.replace(/\s+/g, ' ').toLowerCase();
  if (!text.includes('welcome to prairi')) return false;
  if (!text.includes('3.1') && !text.includes('3,1')) return false;
  return (
    text.includes('feedback') ||
    text.includes('write us right here') ||
    text.includes('advanced platform')
  );
}

function prairiBrandSignOff(body: string): string {
  return body.replace(/The PRAIRI Team/gi, 'P R A I R I').replace(/PRAIRI Team/gi, 'P R A I R I');
}

export function displayPrairiTeamWelcomeBody(
  body: string,
  isPrairiTeam: boolean,
  viewer: PrairiViewerType = 'investor'
): string {
  const source = isPrairiTeam && isLegacyPrairiTeamWelcome(body) ? welcomeBodyFor(viewer) : body;
  if (!isPrairiTeam) return source;
  if (
    source === PRAIRI_TEAM_NEW_STARTUP_WELCOME_MESSAGE ||
    source === PRAIRI_TEAM_NEW_INVESTOR_WELCOME_MESSAGE
  ) {
    return source;
  }
  return prairiBrandSignOff(source);
}

export function displayPrairiTeamWelcomePreview(
  preview: string,
  isPrairiTeam: boolean,
  viewer: PrairiViewerType = 'investor'
): string {
  if (!isPrairiTeam) return preview;
  const text = preview.replace(/\s+/g, ' ').toLowerCase();
  if (
    isLegacyPrairiTeamWelcome(preview) ||
    text.includes('prairi 3.1') ||
    text.includes('prairi v3.1 is here') ||
    text.includes("discover what's new for investors") ||
    text.includes('smarter matching') ||
    text.includes('get discovered by the right investors')
  ) {
    return welcomePreviewFor(viewer);
  }
  return preview;
}

export function isLocalPrairiTeamWelcomeConversation(conversationId: string): boolean {
  return conversationId === SYNTHETIC_PRAIRI_TEAM_CONVERSATION_ID;
}

export type PrairiTeamCatalogMatch = {
  id: string;
  companyName?: string | null;
  profilePictureUrl?: string | null;
};

export function buildPrairiTeamFallbackProfile(): StartupProfile {
  const now = new Date();
  return {
    id: SYNTHETIC_PRAIRI_TEAM_STARTUP_ID,
    userId: SYNTHETIC_PRAIRI_TEAM_STARTUP_ID,
    companyName: 'PRAIRI Team',
    slogan: 'Redefining fundraising for the 21st century',
    url: 'https://prairi.com',
    country: 'Global',
    pitchSummary:
      'PRAIRI is a relevance-based matching platform that displays startups to investors through personalized matching.',
    industry: 'All industries' as StartupProfile['industry'],
    businessModel: 'b2b-b2c',
    productFocus: 'other',
    stage: 'Any stage' as StartupProfile['stage'],
    investment: 0,
    currency: 'usd',
    companyFounders: 1,
    traction: '',
    impressiveFactor:
      'PRAIRI now has users in over 38 countries. Investors see relevant startups conveniently displayed side-by-side with decks and profiles.',
    foundersData: [{ name: 'Peter Fosso', country: '', experience: '' }],
    profileCompleted: true,
    currentStep: 8,
    lastCompletedStep: 8,
    lastUpdatedAt: now,
    createdAt: now,
    updatedAt: now,
    approved: true,
    paused: false,
  };
}

/** Stand-in investor card beside the new-startup welcome, until PRAIRI has a real investor account. */
export function buildPrairiTeamFallbackInvestorProfile(): InvestorProfile {
  const now = new Date();
  return {
    id: SYNTHETIC_PRAIRI_TEAM_STARTUP_ID,
    userId: SYNTHETIC_PRAIRI_TEAM_STARTUP_ID,
    companyName: 'PRAIRI Team',
    country: 'Global',
    previousInvestments: '',
    investAs: [],
    paysFee: false,
    countries: [],
    states: [],
    industries: [],
    businessModelPreference: [],
    productFocusPreference: [],
    stages: [],
    currency: 'usd',
    investmentRange: [0, 0],
    expertise: [],
    profileCompleted: true,
    currentStep: 8,
    lastCompletedStep: 8,
    lastUpdatedAt: now,
    createdAt: now,
    updatedAt: now,
    approved: true,
    paused: false,
  };
}

export function buildPrairiTeamWelcomeMessage(options: {
  investorId: string;
  startupId: string;
  /** Who is reading. Startups get the startup message. Default is investor. */
  viewer?: PrairiViewerType;
  /** A newly registered user with no PRAIRI thread gets the new-user welcome. */
  newAccount?: boolean;
}): ConversationMessage {
  const viewer = options.viewer ?? 'investor';
  return {
    id: `${SYNTHETIC_PRAIRI_TEAM_CONVERSATION_ID}-message`,
    senderId: viewer === 'startup' ? options.investorId : options.startupId,
    recipientId: viewer === 'startup' ? options.startupId : options.investorId,
    senderType: viewer === 'startup' ? 'investor' : 'startup',
    body: options.newAccount
      ? viewer === 'startup'
        ? PRAIRI_TEAM_NEW_STARTUP_WELCOME_MESSAGE
        : PRAIRI_TEAM_NEW_INVESTOR_WELCOME_MESSAGE
      : welcomeBodyFor(viewer),
    createdAt: SYNTHETIC_PRAIRI_TEAM_WELCOME_AT,
    readAt: null,
    delivery: null,
  };
}

export function withoutPrairiTeamConversation<
  T extends { otherUser?: { name?: string | null; companyName?: string | null } },
>(conversations: T[]): T[] {
  return conversations.filter(item => !isPrairiTeamParticipant(item.otherUser || {}));
}

function conversationRecency(item: { lastMessageAt?: string | null }): number {
  const ms = Date.parse(item.lastMessageAt || '');
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * Real PRAIRI welcome threads are stored with a recent lastMessageAt, which pins them
 * to the top. Keep them at the synthetic old date until the investor replies.
 */
function prairiThreadListLastMessageAt(item: InboxConversationItem, investorId: string): string {
  if (item.lastSenderId && item.lastSenderId === investorId) {
    return item.lastMessageAt || SYNTHETIC_PRAIRI_TEAM_WELCOME_AT;
  }
  return SYNTHETIC_PRAIRI_TEAM_WELCOME_AT;
}

/** Left inbox: newest conversation first. Message text inside a thread is unchanged. */
export function orderInboxConversationsNewestFirst<T extends { lastMessageAt?: string | null }>(
  items: T[]
): T[] {
  return [...items].sort((a, b) => conversationRecency(b) - conversationRecency(a));
}

/**
 * Thread to open when the messages page loads.
 * The conversation they were last looking at wins. Otherwise the newest real thread,
 * so a first visit does not land on the PRAIRI welcome.
 */
export function inboxConversationToOpen(
  conversations: Array<{
    conversationId: string;
    otherUser?: { name?: string | null; companyName?: string | null };
  }>,
  lastViewedId?: string | null
): string | null {
  if (lastViewedId && conversations.some(item => item.conversationId === lastViewedId)) {
    return lastViewedId;
  }
  return preferredInboxConversationId(conversations);
}

/** Newest first already; pick the first real thread so PRAIRI welcome is not the default open. */
export function preferredInboxConversationId(
  conversations: Array<{
    conversationId: string;
    otherUser?: { name?: string | null; companyName?: string | null };
  }>
): string | null {
  if (!conversations.length) return null;
  const real = conversations.find(item => !isPrairiTeamParticipant(item.otherUser || {}));
  return (real || conversations[0]).conversationId;
}

export function withPrairiTeamWelcomeConversation(
  conversations: InboxConversationItem[],
  options: {
    investorId?: string;
    team?: PrairiTeamCatalogMatch | null;
    prairiTeamOnline?: boolean;
    viewer?: PrairiViewerType;
    /** A newly registered user with no PRAIRI thread gets the new-user welcome. */
    newAccount?: boolean;
  }
): InboxConversationItem[] {
  if (!options.investorId) return conversations;
  const investorId = options.investorId;
  const viewer = options.viewer ?? 'investor';

  const existing = conversations.find(item => isPrairiTeamParticipant(item.otherUser));
  if (existing) {
    return orderInboxConversationsNewestFirst(
      conversations.map(item =>
        item.conversationId === existing.conversationId
          ? {
              ...item,
              lastMessageAt: prairiThreadListLastMessageAt(item, investorId),
              lastMessagePreview: displayPrairiTeamWelcomePreview(
                item.lastMessagePreview,
                true,
                viewer
              ),
            }
          : item
      )
    );
  }
  if (viewer === 'startup') {
    // A startup with no PRAIRI thread yet: the welcome is kept on this device.
    const now = SYNTHETIC_PRAIRI_TEAM_WELCOME_AT;
    const injectedForStartup: InboxConversationItem = {
      conversationId: SYNTHETIC_PRAIRI_TEAM_CONVERSATION_ID,
      favoriteId: SYNTHETIC_PRAIRI_TEAM_CONVERSATION_ID,
      investorId: SYNTHETIC_PRAIRI_TEAM_STARTUP_ID,
      startupId: investorId,
      otherUser: {
        id: SYNTHETIC_PRAIRI_TEAM_STARTUP_ID,
        name: 'PRAIRI Team',
        userType: 'investor',
        companyName: 'PRAIRI Team',
        isOnline: options.prairiTeamOnline === true,
      },
      lastMessageAt: now,
      lastMessagePreview: options.newAccount
        ? PRAIRI_TEAM_NEW_STARTUP_WELCOME_PREVIEW
        : PRAIRI_TEAM_STARTUP_WELCOME_PREVIEW,
      lastSenderId: SYNTHETIC_PRAIRI_TEAM_STARTUP_ID,
      unreadCount: 0,
      isActive: true,
      favoriteActive: true,
      updatedAt: now,
    };
    return orderInboxConversationsNewestFirst([injectedForStartup, ...conversations]);
  }

  const startupId = options.team?.id || SYNTHETIC_PRAIRI_TEAM_STARTUP_ID;
  const companyName = options.team?.companyName || 'PRAIRI Team';
  const now = SYNTHETIC_PRAIRI_TEAM_WELCOME_AT;

  const injected: InboxConversationItem = {
    conversationId: SYNTHETIC_PRAIRI_TEAM_CONVERSATION_ID,
    favoriteId: SYNTHETIC_PRAIRI_TEAM_CONVERSATION_ID,
    investorId,
    startupId,
    otherUser: {
      id: startupId,
      name: 'PRAIRI Team',
      userType: 'startup',
      companyName,
      profilePictureUrl: options.team?.profilePictureUrl || undefined,
      isOnline: options.prairiTeamOnline === true,
    },
    lastMessageAt: now,
    lastMessagePreview: options.newAccount
      ? PRAIRI_TEAM_NEW_INVESTOR_WELCOME_PREVIEW
      : PRAIRI_TEAM_WELCOME_PREVIEW,
    lastSenderId: startupId,
    unreadCount: 0,
    isActive: true,
    favoriteActive: true,
    updatedAt: now,
  };

  return orderInboxConversationsNewestFirst([injected, ...conversations]);
}
